const normalizeRoleName = (roleName) =>
  typeof roleName === 'string' ? roleName.trim().toLowerCase() : '';

// IDs conhecidos do backend para níveis de acesso (ajuste aqui se o backend adicionar novos)
const SUPERADMIN_ROLE_IDS = [1]; // tipicamente "Super Admin"
const ADMIN_ROLE_IDS = [2]; // tipicamente "Admin"
const ADMIN_PERMISSION_SLUGS = [
  'companies:manage',
  'attendants:manage',
  'teams:manage',
  'whatsapp:manage',
  'groups:manage',
  'analytics:view',
  'client_updates:manage',
];
const normalizePermissions = (perms) => {
  if (Array.isArray(perms)) {
    return perms.filter((p) => typeof p === 'string').map((p) => p.toLowerCase());
  }
  if (perms && typeof perms === 'object') {
    return Object.entries(perms)
      .filter(([, value]) => !!value)
      .map(([key]) => String(key).toLowerCase());
  }
  return [];
};

export const isSuperAdminRoleName = (roleName) =>
  /\bsuper[\s_-]*admin\b/.test(normalizeRoleName(roleName));

export const isAgentRoleName = (roleName) =>
  /\b(agent|agente)\b/.test(normalizeRoleName(roleName));

export const isGlobalAdminRoleName = (roleName) =>
  isSuperAdminRoleName(roleName) || isAgentRoleName(roleName);

export const isAdminRoleName = (roleName) =>
  /\b(super[\s_-]*admin|admin)\b/.test(normalizeRoleName(roleName));

const normalizeOptionalNumber = (value) => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export const attendantHasPermission = (attendantData = {}, permission) => {
  if (!permission) return false;
  const slug = String(permission).toLowerCase();
  if (slug === '') return false;
  // Super Admin tem tudo
  const roleName =
    attendantData?.role?.name ||
    attendantData?.role_name ||
    attendantData?.roleName ||
    '';
  if (isSuperAdminRoleName(roleName)) return true;
  const perms = normalizePermissions(attendantData?.permissions || attendantData?.permission_names);
  return perms.includes(slug);
};

/**
 * Resolve privilégios de admin/superadmin a partir de dados do atendente.
 * Usa role atual, slugs, flags e permissões para determinar o nível.
 */
const resolveRoleNameFromId = (roleId, roleCatalog = null, roleData = null) => {
  const normalizedId = normalizeOptionalNumber(roleId);
  if (normalizedId === null) return null;
  if (roleData && normalizeOptionalNumber(roleData?.id) === normalizedId) {
    return roleData?.name || roleData?.slug || null;
  }
  if (Array.isArray(roleCatalog)) {
    const hit = roleCatalog.find((r) => normalizeOptionalNumber(r?.id) === normalizedId);
    if (hit?.name || hit?.slug) return hit.name || hit.slug;
  } else if (roleCatalog && typeof roleCatalog === 'object') {
    const hit = roleCatalog[normalizedId] || roleCatalog[String(normalizedId)];
    if (hit?.name || hit?.slug) return hit.name || hit.slug;
  }
  return null;
};

export const resolveAdminPrivileges = (
  attendantData = {},
  fallbackRoleName = null,
  roleIdFromContext = null,
  roleCatalog = null,
  roleData = null
) => {
  const roleCandidates = [];
  if (fallbackRoleName) roleCandidates.push(fallbackRoleName);
  const resolvedFromId = resolveRoleNameFromId(roleIdFromContext, roleCatalog, roleData);
  if (resolvedFromId) roleCandidates.push(resolvedFromId);
  if (attendantData?.role?.name) roleCandidates.push(attendantData.role.name);
  if (attendantData?.role?.slug) roleCandidates.push(attendantData.role.slug);
  if (attendantData?.role_name) roleCandidates.push(attendantData.role_name);
  if (attendantData?.roleName) roleCandidates.push(attendantData.roleName);
  if (typeof attendantData?.role === 'string') roleCandidates.push(attendantData.role);
  if (Array.isArray(attendantData?.roles)) {
    attendantData.roles.forEach((role) => {
      if (typeof role === 'string') roleCandidates.push(role);
      else if (role?.name) roleCandidates.push(role.name);
      else if (role?.slug) roleCandidates.push(role.slug);
    });
  }

  const roleIsSuperAdmin = roleCandidates.some(isSuperAdminRoleName);
  const roleIsAgent = roleCandidates.some(isAgentRoleName);
  const roleIsAdmin = roleCandidates.some(isAdminRoleName);

  const superAdminFlag = [
    attendantData?.is_superadmin,
    attendantData?.isSuperAdmin,
    attendantData?.superadmin,
    attendantData?.is_super_user,
    attendantData?.super_user,
    attendantData?.superuser,
  ].some(Boolean);

  const adminFlag = [
    attendantData?.is_admin,
    attendantData?.isAdmin,
    attendantData?.is_owner,
    attendantData?.owner,
    attendantData?.is_manager,
    attendantData?.manager,
  ].some(Boolean);

  const permissionsHit = normalizePermissions(attendantData?.permissions).some((p) =>
    ADMIN_PERMISSION_SLUGS.includes(p) ||
    /\b(superuser|manage_all_attend(ance|ment)s?|attend(ance|ment)s?:manage_all|attendance:cancel|attendances:cancel|cancel[_-]?attendance|slots?:cancel)\b/.test(p)
  );

  const roleId =
    normalizeOptionalNumber(attendantData?.role_id) ??
    normalizeOptionalNumber(attendantData?.role?.id) ??
    null;

  const roleIdSuper = roleId !== null && SUPERADMIN_ROLE_IDS.includes(roleId);
  const roleIdAdmin =
    roleId !== null && (ADMIN_ROLE_IDS.includes(roleId) || SUPERADMIN_ROLE_IDS.includes(roleId));

  const isSuperAdmin = roleIsSuperAdmin || superAdminFlag || roleIdSuper;
  const isAgent = roleIsAgent;
  const isGlobalAdmin = isSuperAdmin || isAgent;
  const isAdmin = isGlobalAdmin || roleIsAdmin || adminFlag || permissionsHit || roleIdAdmin;

  return { isAdmin, isSuperAdmin, isAgent, isGlobalAdmin };
};

export default {
  normalizeRoleName,
  isAdminRoleName,
  isAgentRoleName,
  isGlobalAdminRoleName,
  isSuperAdminRoleName,
  resolveAdminPrivileges,
};
